import { FileText, Heart, Activity } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/ui/card'
import { usePortalMedicalRecords } from '@hooks/usePortal'

export function PortalRecordsPage() {
  const { data: records, isLoading } = usePortalMedicalRecords()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Medical Records</h1>
        <p className="text-muted-foreground mt-1">View your health information</p>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="h-32 animate-pulse"></Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-medical-600" />
                Health Summary
              </CardTitle>
              <CardDescription>Your current health status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Blood Type</p>
                  <p className="text-lg font-semibold">{records?.blood_type || 'Not recorded'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Height</p>
                  <p className="text-lg font-semibold">{records?.height_cm ? `${records.height_cm} cm` : 'Not recorded'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Weight</p>
                  <p className="text-lg font-semibold">{records?.weight_kg ? `${records.weight_kg} kg` : 'Not recorded'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">BMI</p>
                  <p className="text-lg font-semibold">{records?.bmi || 'Not calculated'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-medical-600" />
                Conditions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {records?.conditions?.length ? (
                <ul className="space-y-2">
                  {records.conditions.map((condition, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-medical-500" />
                      {condition}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No conditions recorded</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-medical-600" />
                Allergies
              </CardTitle>
            </CardHeader>
            <CardContent>
              {records?.allergies?.length ? (
                <ul className="space-y-2">
                  {records.allergies.map((allergy, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-red-500" />
                      {allergy}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No allergies recorded</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-medical-600" />
                Current Medications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {records?.current_medications?.length ? (
                <ul className="space-y-2">
                  {records.current_medications.map((med, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500" />
                      {med}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No medications recorded</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
